"use client";

import React, { useState, useMemo, useCallback } from "react";
import { useNewsStore } from "@/vybe-trader-client/news/stores/useNewsStore";
import {
  type NewsFilterType,
  filterNewsArticles,
  formatPublishedAt,
  ImportanceMark,
  listHeadline,
} from "@/vybe-trader-client/news/newsView";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { ExternalLink, Filter, Newspaper, Search } from "lucide-react";
import type { NewsArticle } from "@/vybe-trader-client/news/stores/useNewsStore";
import { EmptyBlock } from "@/components/common/EmptyBlock";

export default function NewsDesktopContent() {
  const articles = useNewsStore((s) => s.articles);
  const markRead = useNewsStore((s) => s.markRead);
  const isRead = useNewsStore((s) => s.isRead);

  const [filterType, setFilterType] = useState<NewsFilterType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () => filterNewsArticles(articles, filterType, searchQuery),
    [articles, filterType, searchQuery]
  );

  const selected = useMemo(
    () => filtered.find((a) => a.id === selectedId) ?? articles.find((a) => a.id === selectedId) ?? null,
    [filtered, articles, selectedId]
  );

  const unreadCount = useMemo(
    () => articles.filter((a) => !isRead(a.id)).length,
    [articles, isRead]
  );

  const handleSelect = useCallback(
    (article: NewsArticle) => {
      setSelectedId(article.id);
      markRead(article.id);
    },
    [markRead]
  );

  const filterLabel =
    filterType === "all"
      ? "All"
      : filterType === "breaking"
        ? "Breaking"
        : "Market moving";

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-2">
        <span className="shrink-0 text-xs font-semibold">News</span>
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-6 pl-7 text-[11px]"
          />
        </div>
        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {filtered.length}
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 shrink-0 px-2 text-xs">
              <Filter className="mr-1 h-3 w-3" />
              {filterLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel className="text-xs">Show</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={filterType === "all"}
              onCheckedChange={() => setFilterType("all")}
              className="text-xs"
            >
              All articles
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "breaking"}
              onCheckedChange={() => setFilterType("breaking")}
              className="text-xs"
            >
              Breaking only
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "market_moving"}
              onCheckedChange={() => setFilterType("market_moving")}
              className="text-xs"
            >
              Market moving
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex w-[46%] min-w-[160px] max-w-[300px] flex-col border-r border-border">
          <div
            className="min-h-0 flex-1 overflow-y-auto"
            role="listbox"
            aria-label="News headlines"
            aria-activedescendant={selectedId ? `news-item-${selectedId}` : undefined}
            data-roving-container
          >
            {filtered.length === 0 ? (
              <EmptyBlock
                config={{
                  glyph: "▤",
                  message:
                    searchQuery || filterType !== "all"
                      ? "No articles match your filters"
                      : "No articles",
                }}
              />
            ) : (
              filtered.map((article, index) => {
                const active = article.id === selectedId;
                const unread = !isRead(article.id);
                const { date, time } = formatPublishedAt(article.publishedAtUnix);
                const isRoving = active || (selectedId == null && index === 0);
                const moveFocus = (target: number) => {
                  const clamped = Math.max(0, Math.min(filtered.length - 1, target));
                  handleSelect(filtered[clamped]);
                  const el = document.querySelectorAll<HTMLElement>(
                    '[data-news-item]',
                  )[clamped];
                  el?.focus();
                };
                return (
                  <button
                    key={article.id}
                    id={`news-item-${article.id}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-news-item
                    data-roving-item
                    tabIndex={isRoving ? 0 : -1}
                    onClick={() => handleSelect(article)}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        moveFocus(index + 1);
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        moveFocus(index - 1);
                      } else if (e.key === "Home") {
                        e.preventDefault();
                        moveFocus(0);
                      } else if (e.key === "End") {
                        e.preventDefault();
                        moveFocus(filtered.length - 1);
                      }
                    }}
                    className={`flex w-full items-center gap-1.5 border-b border-border px-2 py-1.5 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
                      active ? "bg-muted/50" : unread ? "bg-muted/20" : ""
                    }`}
                  >
                    {unread ? (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    ) : (
                      <span className="w-1.5 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span
                          className={`truncate text-[11px] ${
                            unread ? "font-semibold text-foreground" : "text-foreground/90"
                          }`}
                        >
                          {listHeadline(article)}
                        </span>
                        <ImportanceMark
                          importance={article.importance}
                          isBreaking={article.isBreaking}
                        />
                      </div>
                      <span className="block truncate text-[10px] text-muted-foreground">
                        {article.category}
                        {article.provider ? ` · ${article.provider}` : ""}
                      </span>
                    </div>
                    <div className="flex shrink-0 flex-col items-end text-[10px] leading-tight tabular-nums text-muted-foreground">
                      <span>{date}</span>
                      <span>{time}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          {selected ? (
            <>
              <div className="flex h-7 shrink-0 items-center justify-between gap-2 border-b border-border px-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">{selected.headline}</p>
                  <p className="truncate text-[10px] text-muted-foreground">
                    {selected.category}
                    {selected.subcategory ? ` / ${selected.subcategory}` : ""}
                    {" · "}
                    {formatPublishedAt(selected.publishedAtUnix).date}{" "}
                    {formatPublishedAt(selected.publishedAtUnix).time}
                  </p>
                </div>
                {selected.url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 shrink-0 px-1.5 text-xs"
                    asChild
                  >
                    <a href={selected.url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </Button>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2 space-y-2">
                {selected.imageUrl && (
                  <img
                    src={selected.imageUrl}
                    alt=""
                    className="max-h-32 w-full rounded object-cover"
                  />
                )}
                {selected.summary && (
                  <p className="text-[11px] font-medium text-foreground/90">{selected.summary}</p>
                )}
                <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-foreground/80">
                  {selected.body || selected.summary}
                </p>
                <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
                  {selected.isBreaking && <span className="text-destructive">Breaking</span>}
                  {selected.isMarketMoving && <span>Market moving</span>}
                  {selected.isRumor && <span>Rumor</span>}
                  {selected.sentiment !== 0 && (
                    <span>Sentiment {selected.sentiment.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-1 text-center">
              <Newspaper className="h-5 w-5 text-muted-foreground/40" />
              <p className="text-[11px] text-muted-foreground">Select an article</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
